import { useEffect, useState } from 'react';
import { fieldClass, labelClass } from '@timely/ui';
import type { Customer } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';

interface Member { id: string; name: string }

/**
 * Who works an account, changed from the account (PRODUCT-UX-FOUNDATION §12).
 *
 * The owner comes from the sheet, through the owner-code mapping, and is the
 * sheet's to change. The collector is the company's own choice: somebody
 * other than the owner who chases this account. Setting it is an assignment,
 * so the database's column guard asks for `customers.assign` whatever this
 * screen does, and the row policy asks for `customers.edit` and sight of the
 * account; the screen offers the control only to somebody holding both.
 */
export function AssignCollector({ customer, workspaceId, disabled, onAssigned, onError }: {
    customer: Customer;
    workspaceId: string;
    disabled: boolean;
    onAssigned: (name: string | null) => Promise<void>;
    onError: (message: string) => void;
}) {
    const members = useActiveMembers(workspaceId);
    const [saving, setSaving] = useState(false);
    const current = (customer.collectorMemberId as string | undefined) ?? '';
    const owner = members.find(m => m.id === (customer.ownerMemberId as string | undefined));

    const choose = async (memberId: string) => {
        setSaving(true);
        const { error } = await supabase()
            .from('customers').update({ collector_member_id: memberId || null })
            .eq('id', customer.id).eq('workspace_id', workspaceId);
        setSaving(false);
        if (error) {
            onError(/row-level security|permission denied|not allowed/i.test(error.message)
                ? 'You do not have permission to reassign this account.'
                : 'That did not save. Try again in a moment.');
            return;
        }
        await onAssigned(members.find(m => m.id === memberId)?.name ?? null);
    };

    return (
        <div className="py-1">
            <label htmlFor="assign-collector" className={labelClass}>Collector</label>
            <select
                id="assign-collector"
                value={current}
                disabled={disabled || saving || members.length === 0}
                onChange={e => { void choose(e.target.value); }}
                className={fieldClass({ className: 'w-full' })}
            >
                <option value="">{owner ? `None — ${owner.name}, the owner, works it` : 'None — the owner works it'}</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
        </div>
    );
}

/** The people an account can be given to: this company's active members, by name. */
function useActiveMembers(workspaceId: string): Member[] {
    const [members, setMembers] = useState<Member[]>([]);
    useEffect(() => {
        let current = true;
        void supabase().from('workspace_members').select('id, display_name, code, status').eq('workspace_id', workspaceId)
            .then(({ data }) => {
                if (!current || !data) return;
                const rows = data as { id: string; display_name: string | null; code: string | null; status: string }[];
                setMembers(rows.filter(r => r.status === 'active')
                    .map(r => ({ id: r.id, name: r.display_name || r.code || 'Somebody' }))
                    .sort((a, b) => a.name.localeCompare(b.name)));
            });
        return () => { current = false; };
    }, [workspaceId]);
    return members;
}
